/**
 * Helper utility to determine review button state based on user role and BOQ status
 */

export type BOQStatus =
  | "In-progress"
  | "Under Review - Senior Estimator"
  | "Under Review Commercial Director"
  | "Under Client Review";

export type UserRole =
  | "Estimator"
  | "Senior Estimator"
  | "Commercial Director"
  | string;

export interface ReviewButtonState {
  buttonText: string;
  nextStatus: BOQStatus | null;
  isDisabled: boolean;
  modalTitle: string;
  modalMessage: string;
  shouldShow: boolean;
}

/**
 * Determines the review button state based on current BOQ status and user role
 * @param currentStatus - Current BOQ status
 * @param userRole - Current user's role
 * @returns ReviewButtonState object with button configuration
 */
export function getReviewButtonState(
  currentStatus: string | undefined,
  userRole: string | null | undefined
): ReviewButtonState {
  // Debug logging

  // Default state - button should be hidden if status/role is unknown
  const defaultState: ReviewButtonState = {
    buttonText: "Send for Review",
    nextStatus: null,
    isDisabled: true,
    modalTitle: "Send Tender for Review",
    modalMessage: "Unable to determine review status.",
    shouldShow: false,
  };

  // If we don't have both status and role, hide the button
  if (!currentStatus || !userRole) {
    return defaultState;
  }

  // Normalize strings for comparison (trim whitespace, lowercase for flexible matching)
  const normalizedStatus = currentStatus.trim().toLowerCase();
  const normalizedRole = userRole.trim().toLowerCase();


  // Case 1: Status is "In-progress" and role is Estimator
  if (normalizedStatus === "in-progress" && normalizedRole === "estimator") {
    return {
      buttonText: "Submit for Review Senior Estimator",
      nextStatus: "Under Review - Senior Estimator",
      isDisabled: false,
      modalTitle: "Submit for Review",
      modalMessage:
        "Do you want to submit this tender to the Senior Estimator for review?",
      shouldShow: true,
    };
  }

  // Case 2: Status is "Under Review - Senior Estimator" and role is Senior Estimator
  // Support multiple variations of the status string
  const isSeniorEstimatorReviewStatus =
    normalizedStatus === "under review - senior estimator" ||
    normalizedStatus === "under review-senior estimator" ||
    normalizedStatus === "under review senior estimator" ||
    (normalizedStatus.includes("senior estimator") &&
      normalizedStatus.includes("review"));

  const isSeniorEstimatorRole =
    normalizedRole === "senior estimator" ||
    normalizedRole === "seniorestimator";


  if (isSeniorEstimatorReviewStatus && isSeniorEstimatorRole) {
    return {
      buttonText: "Submit for Review Commercial Director",
      nextStatus: "Under Review Commercial Director",
      isDisabled: false,
      modalTitle: "Submit for Review",
      modalMessage:
        "Do you want to submit this tender to the Commercial Director for review?",
      shouldShow: true,
    };
  }

  // Case 3: Status is "Under Review Commercial Director" and role is Commercial Director
  const isCommercialDirectorReviewStatus =
    normalizedStatus === "under review commercial director" ||
    normalizedStatus === "under review - commercial director" ||
    (normalizedStatus.includes("commercial director") &&
      normalizedStatus.includes("review"));

  const isCommercialDirectorRole =
    normalizedRole === "commercial director" ||
    normalizedRole === "commercialdirector";

  if (isCommercialDirectorReviewStatus && isCommercialDirectorRole) {
    return {
      buttonText: "Submit for Client Review",
      nextStatus: "Under Client Review",
      isDisabled: false,
      modalTitle: "Submit for Client Review",
      modalMessage: "Do you want to submit this tender for client review?",
      shouldShow: true,
    };
  }

  // Case 4: Status is "Under Client Review" and role is Estimator
  const isClientReviewStatus =
    normalizedStatus === "under client review" ||
    (normalizedStatus.includes("client") &&
      normalizedStatus.includes("review"));

  if (isClientReviewStatus && normalizedRole === "estimator") {
    return {
      buttonText: "Submitted for Client Review",
      nextStatus: null,
      isDisabled: true,
      modalTitle: "Already Submitted",
      modalMessage: "This tender has already been submitted for client review.",
      shouldShow: false,
    };
  }

  // For any other combination, hide the button
  return {
    ...defaultState,
    shouldShow: false,
  };
}

/**
 * Get initial status for a new BOQ
 */
export function getInitialBOQStatus(): BOQStatus {
  return "In-progress";
}

/**
 * Check if a user can perform a review action based on their role
 */
export function canUserReview(userRole: string | null | undefined): boolean {
  return (
    userRole === "Estimator" ||
    userRole === "Senior Estimator" ||
    userRole === "Commercial Director"
  );
}
